<script>
import Layout from '@layouts/main';
import VipModel from '@models/Vip';
import debounce from "lodash/debounce";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import HeaderComponent from '@components/HeaderComponent';

export default {
  name: 'VipsIndex',
  metaInfo() {
    return {
      title: `Admin - Vips`,
    };
  },
  components: {
    Layout,
    FontAwesomeIcon,
    HeaderComponent,
  },
  data() {
    return {
      sortBy: 'name',
      sortDesc: false,
      totalRows: null,
      perPage: 20,
      currentPage: 1,
      fieldsTable: [{ key: 'id', sortable: true }, { key: 'name', label: 'Name', sortable: true }, { key: 'actions', label: 'Actions' }],
      filters: {
        name: null,
      },
    };
  },
  computed: {
    sortDir() {
      return this.sortDesc ? '-' : '';
    },
  },
  watch: {
    filters: {
      handler() {
        this.$refs.asyncTable.refresh();
      },
      immediate: false,
      deep: true,
    },
  },
  methods: {
    itemsProvider(ctx) {
      return VipModel.orderBy(this.sortDir + ctx.sortBy)
      .where('name', this.filters.name)
      .page(this.currentPage)
      .limit(ctx.perPage).get()
      .then((response) => {
        const items = response.data;
        this.totalRows = response.total;
        return items || [];
      }).catch(() => []);
    },
    updateFilter: debounce(function(value) {
      this.filters.name = value;
    }, 500),
    async deleteEntity(id) {
      // eslint-disable-next-line
      if (confirm(this.$t('alerts.delete_entity', { entity: this.$t('entities.vip') }))) {
        try {
          this.$wait.start('vip processing');
          const vip = new VipModel({ id });
          await vip.delete();
          this.$refs.asyncTable.refresh();
        } catch (error) {
        } finally {
          this.$wait.end('vip processing');
        }
      }
    },
    restore(id) {
      if (confirm(this.$t('alerts.restore_entity', { entity: this.$t('entities.vip') }))) {
        this.$wait.start('vip processing');
        new VipModel({
          id: `${id}/restore`,
        })
          .save()
          .then(() => {
            this.$refs.asyncTable.refresh();
            this.$wait.end('vip processing');
          })
          .catch(() => {
            this.$wait.end('vip processing');
          });
      }
    },
  },
};
</script>

<template>
  <Layout>
    <b-row class="mb-4">
      <b-col>
        <HeaderComponent
          :title="$t('titles.vips')"
          :button-text="$t('titles.create_vip')"
          @button-pressed="$router.push({ name: 'vips.create' })"
        ></HeaderComponent>
      </b-col>
    </b-row>
    <b-row class="mb-4">
      <b-col sm="12" md="12" lg="4">
        <HeaderComponent>
          <template v-slot:header>
            <span>{{ $t('fields.search') }}</span>&nbsp;
            <small class="pt-01">
              {{ $t('search.message', { entity: $t('search.entities.name') }) }}
            </small>
          </template>
        </HeaderComponent>
        <b-input-group>
          <b-form-input :value="filters.name" class="form-control-sm" :placeholder="$t('placeholders.search')" @input="updateFilter">&nbsp;</b-form-input>
          <b-input-group-append>
            <b-btn variant="secondary" size="sm" class="text-white border-gray" :disabled="!filters.name" @click="filters.name = ''">{{ $t('actions.clear') }}</b-btn>
          </b-input-group-append>
        </b-input-group>
      </b-col>
    </b-row>
    <b-table ref="asyncTable"
             small
             responsive striped hover
             :current-page="currentPage"
             :per-page="perPage"
             :no-sort-reset="true"
             :show-empty="true"
             :items="itemsProvider"
             :sort-by.sync="sortBy"
             :sort-desc.sync="sortDesc"
             :fields="fieldsTable">
      <!-- custom sorting icons -->
      <template #head()="{ label, field: { key, sortable } }">
            {{ label }}
            <template v-if="sortable">
               <!-- Custom icons -->
              <template>
                <FontAwesomeIcon v-if="sortBy !== key" :icon="['fas', 'arrows-alt-v']" />
                <FontAwesomeIcon v-else-if="sortDesc" :icon="['fas', 'arrow-down']" />
                <FontAwesomeIcon v-else :icon="['fas', 'arrow-up']" />
              </template>
            </template>
      </template>
      <template v-slot:cell(actions)="row">
        <div v-if="row.item.deleted_at == undefined || row.item.deleted_at == null">
          <b-btn class="mr-2" size="sm" variant="outline-primary" :to="{ name: 'vips.edit', params: { id: row.item.id } }">
            <FontAwesomeIcon :icon="['fas', 'edit']" />
          </b-btn>
          <b-btn size="sm" variant="outline-danger" @click.stop="deleteEntity(row.item.id)">
            <FontAwesomeIcon :icon="['fas', 'times-circle']" />
          </b-btn>
        </div>
        <div v-else>
          <b-btn class="mr-2" size="sm" variant="outline-success" @click="restore(row.item.id)">
            <FontAwesomeIcon :icon="['fas', 'trash-restore']" />
          </b-btn>
        </div>
      </template>
    </b-table>
    <b-row>
      <b-col sm="12">
        <div class="d-flex justify-content-between align-items-center mt-2">
          <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" />
        </div>
      </b-col>
    </b-row>
  </Layout>
</template>
